import API_BASE_URL from '../../config/api';
import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Chip,
    Pagination,
    Alert,
    CircularProgress,
    Switch,
    FormControlLabel,
    Stack,
    Fab,
    useTheme,
    useMediaQuery,
    Collapse,
    InputAdornment,
    Divider,
    Drawer,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Avatar,
    Tooltip
} from '@mui/material';
import {
    Search as SearchIcon,
    Visibility as ViewIcon,
    Edit as EditIcon,
    FilterList as FilterIcon,
    ViewList as ViewListIcon,
    ViewModule as ViewModuleIcon,
    Close as CloseIcon,
    Clear as ClearIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    ShoppingCart as CartIcon,
    Receipt as OrderIcon,
    Security as SecurityIcon,
    CheckCircle as VerifiedIcon,
    Cancel as UnverifiedIcon,
    Block as BlockIcon,
    CheckCircleOutline as ActiveIcon
} from '@mui/icons-material';

const BASE_URL = API_BASE_URL;

// User Card Component
const UserCard = ({ user, onView, onToggleStatus }) => {
    const getUserStatus = (user) => {
        if (!user.isActive) return { label: 'Inactive', color: 'error' };
        if (user.security?.isEmailVerified) return { label: 'Verified', color: 'success' };
        return { label: 'Active', color: 'warning' };
    };

    return (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: { xs: 1, sm: 2 } }}>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: { xs: 1, sm: 2 }, width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                            {user.profile?.firstName?.charAt(0)}
                        </Avatar>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                {user.profile?.firstName} {user.profile?.familyName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                {user.email}
                            </Typography>
                        </Box>
                    </Box>
                    <Chip
                        label={getUserStatus(user).label}
                        size="small"
                        color={getUserStatus(user).color}
                    />
                </Box>

                <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Country: {user.profile?.country || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Phone: {user.profile?.phone || 'N/A'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                        <IconButton
                            onClick={() => onView(user)}
                            size="small"
                            color="primary"
                        >
                            <ViewIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            onClick={() => onToggleStatus(user)}
                            size="small"
                            color={user.isActive ? 'error' : 'success'}
                        >
                            {user.isActive ? <BlockIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                        </IconButton>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// User Details Dialog Component
const UserDetailDialog = ({ open, onClose, user, orders, cart, tabValue, onTabChange }) => {
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const TabPanel = ({ children, value, index, ...other }) => (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
        </div>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2, m: { xs: 1, sm: 2 } }
            }}
        >
            <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center">
                    <Avatar sx={{ mr: { xs: 1, sm: 2 }, width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                        {user?.profile?.firstName?.charAt(0)}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {user?.profile?.firstName} {user?.profile?.familyName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {user?.email}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: { xs: 1, sm: 2 }, px: { xs: 2, sm: 3 } }}>
                <Tabs value={tabValue} onChange={onTabChange}>
                    <Tab label="Profile" />
                    <Tab label="Orders" />
                    <Tab label="Cart" />
                    <Tab label="Security" />
                </Tabs>

                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                Personal Information
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemText
                                        primary="Full Name"
                                        secondary={`${user?.profile?.firstName || ''} ${user?.profile?.middleName || ''} ${user?.profile?.familyName || ''}`.trim()}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Email"
                                        secondary={user?.email}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Phone"
                                        secondary={user?.profile?.phone}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Country"
                                        secondary={user?.profile?.country}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Nationality"
                                        secondary={user?.profile?.nationality}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Civil ID"
                                        secondary={user?.profile?.civilId}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Gender"
                                        secondary={user?.profile?.gender}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Date of Birth"
                                        secondary={user?.profile?.dateOfBirth ? formatDate(user.profile.dateOfBirth) : 'Not provided'}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                            </List>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                Account Information
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemText
                                        primary="Role"
                                        secondary={user?.role}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Status"
                                        secondary={user?.isActive ? 'Active' : 'Inactive'}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Language"
                                        secondary={user?.preferences?.language}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Currency"
                                        secondary={user?.preferences?.currency}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Last Login"
                                        secondary={user?.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Created"
                                        secondary={formatDate(user?.createdAt)}
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                            </List>
                        </Grid>
                    </Grid>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Recent Orders
                    </Typography>
                    {orders.length > 0 ? (
                        <List>
                            {orders.map((order) => (
                                <ListItem key={order._id} divider>
                                    <ListItemIcon>
                                        <OrderIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`Order #${order.orderNumber}`}
                                        secondary={
                                            <Box>
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    Status: {order.status} | Amount: {order.pricing?.totalAmount}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {formatDate(order.createdAt)}
                                                </Typography>
                                            </Box>
                                        }
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            No orders found
                        </Typography>
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Shopping Cart
                    </Typography>
                    {cart && cart.items?.length > 0 ? (
                        <List>
                            {cart.items.map((item, index) => (
                                <ListItem key={index} divider>
                                    <ListItemIcon>
                                        <CartIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.productId?.name || 'Product'}
                                        secondary={
                                            <Box>
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    Category: {item.productId?.category} |
                                                    Metal: {item.productId?.metal} |
                                                    Karat: {item.productId?.karat}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    Quantity: {item.quantity}
                                                </Typography>
                                            </Box>
                                        }
                                        primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            Cart is empty
                        </Typography>
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={3}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Security Information
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                {user?.security?.isEmailVerified ? <VerifiedIcon color="success" sx={{ fontSize: { xs: 20, sm: 24 } }} /> : <UnverifiedIcon color="error" sx={{ fontSize: { xs: 20, sm: 24 } }} />}
                            </ListItemIcon>
                            <ListItemText
                                primary="Email Verification"
                                secondary={user?.security?.isEmailVerified ? 'Verified' : 'Not Verified'}
                                primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <SecurityIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Two-Factor Authentication"
                                secondary={user?.security?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                                primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemText
                                primary="Login Attempts"
                                secondary={user?.security?.loginAttempts || 0}
                                primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            />
                        </ListItem>
                        {user?.security?.lockUntil && (
                            <ListItem>
                                <ListItemText
                                    primary="Account Locked Until"
                                    secondary={formatDate(user.security.lockUntil)}
                                    primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                />
                            </ListItem>
                        )}
                    </List>
                </TabPanel>
            </DialogContent>
            <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                <Button onClick={onClose} color="inherit">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Main Users Management Component
const Users = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [viewMode, setViewMode] = useState(isMobile ? 'card' : 'table');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [filters, setFilters] = useState({
        search: '',
        isActive: '',
        country: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
    });
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [userDetailDialog, setUserDetailDialog] = useState(false);
    const [statusUpdateDialog, setStatusUpdateDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userOrders, setUserOrders] = useState([]);
    const [userCart, setUserCart] = useState(null);
    const [userDetailTab, setUserDetailTab] = useState(0);
    const [stats, setStats] = useState({ totalUsers: 0, activeUsers: 0, verifiedUsers: 0 });

    useEffect(() => {
        setViewMode(isMobile ? 'card' : 'table');
    }, [isMobile]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page,
                limit: 20,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
                ...(filters.search && { search: filters.search }),
                ...(filters.isActive && { isActive: filters.isActive }),
                ...(filters.country && { country: filters.country })
            });

            const response = await fetch(`${BASE_URL}/api/admin/users?${queryParams}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
                    'Content-Type': 'application/json'
                },
            });

            const data = await response.json();

            if (data.success) {
                setUsers(data.data || []);
                setStats(data.stats || { totalUsers: 0, activeUsers: 0, verifiedUsers: 0 });
                setTotalPages(data.pagination?.pages || 1);
                setTotalUsers(data.pagination?.total || 0);
                setError('');
            } else {
                throw new Error(data.error || 'Failed to fetch users');
            }
        } catch (err) {
            setError('Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const fetchUserDetails = async (userId) => {
        try {
            const response = await fetch(`${BASE_URL}/api/admin/users/${userId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                setSelectedUser(data.data.user);
                setUserOrders(data.data.recentOrders || []);
                setUserCart(data.data.cart);
                setUserDetailDialog(true);
            } else {
                setError(data.error || 'Failed to fetch user details');
            }
        } catch (err) {
            setError('Network error occurred');
        }
    };

    const updateUserStatus = async (userId, isActive) => {
        try {
            const response = await fetch(`${BASE_URL}/api/admin/users/${userId}/status`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ isActive }),
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(`User ${isActive ? 'activated' : 'deactivated'} successfully`);
                setStatusUpdateDialog(false);
                fetchUsers();
                setError('');
            } else {
                throw new Error(data.error || 'Failed to update user status');
            }
        } catch (err) {
            setError('Network error occurred');
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [page, filters]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1);
    };

    const handleClearFilters = () => {
        setFilters({
            search: '',
            isActive: '',
            country: '',
            sortBy: 'createdAt',
            sortOrder: 'desc'
        });
        setPage(1);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getUserStatus = (user) => {
        if (!user.isActive) return { label: 'Inactive', color: 'error' };
        if (user.security?.isEmailVerified) return { label: 'Verified', color: 'success' };
        return { label: 'Active', color: 'warning' };
    };

    const hasActiveFilters = Object.values(filters).some(value => value !== '' && value !== 'createdAt' && value !== 'desc');

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 1,
                mb: { xs: 2, sm: 3 }
            }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                        User Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Manage your users and their account details
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {!isSmallMobile && (
                        <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <IconButton
                                onClick={() => setViewMode('table')}
                                color={viewMode === 'table' ? 'primary' : 'default'}
                                sx={{ borderRadius: 0 }}
                            >
                                <ViewListIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </IconButton>
                            <IconButton
                                onClick={() => setViewMode('card')}
                                color={viewMode === 'card' ? 'primary' : 'default'}
                                sx={{ borderRadius: 0 }}
                            >
                                <ViewModuleIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Alerts */}
            <Collapse in={!!error}>
                <Alert severity="error" onClose={() => setError('')} sx={{ mb: { xs: 1, sm: 2 } }}>
                    {error}
                </Alert>
            </Collapse>

            <Collapse in={!!success}>
                <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: { xs: 1, sm: 2 } }}>
                    {success}
                </Alert>
            </Collapse>
            {/* Mobile Filters Drawer */}
            {isSmallMobile && (
                <Drawer
                    anchor="bottom"
                    open={filtersOpen}
                    onClose={() => setFiltersOpen(false)}
                    PaperProps={{
                        sx: { borderRadius: '16px 16px 0 0', maxHeight: '80vh' }
                    }}
                >
                    <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1, sm: 2 } }}>
                            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Filters</Typography>
                            <IconButton onClick={() => setFiltersOpen(false)}>
                                <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </IconButton>
                        </Box>

                        <Stack spacing={{ xs: 1, sm: 2 }}>
                            <TextField
                                fullWidth
                                label="Search users..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /></InputAdornment>
                                }}
                            />

                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.isActive}
                                    onChange={(e) => handleFilterChange('isActive', e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="true">Active</MenuItem>
                                    <MenuItem value="false">Inactive</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                label="Filter by country"
                                value={filters.country}
                                onChange={(e) => handleFilterChange('country', e.target.value)}
                                size="small"
                            />

                            <FormControl fullWidth size="small">
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                    value={filters.sortBy}
                                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                    label="Sort By"
                                >
                                    <MenuItem value="createdAt">Creation Date</MenuItem>
                                    <MenuItem value="email">Email</MenuItem>
                                    <MenuItem value="profile.firstName">First Name</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>Sort Order</InputLabel>
                                <Select
                                    value={filters.sortOrder}
                                    onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                                    label="Sort Order"
                                >
                                    <MenuItem value="asc">Ascending</MenuItem>
                                    <MenuItem value="desc">Descending</MenuItem>
                                </Select>
                            </FormControl>

                            {hasActiveFilters && (
                                <Button variant="outlined" onClick={handleClearFilters} startIcon={<ClearIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}>
                                    Clear All Filters
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Drawer>
            )}

            {/* Desktop Filters */}
            {!isSmallMobile && (
                <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1, sm: 2 } }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            <FilterIcon /> Filters
                        </Typography>
                        {hasActiveFilters && (
                            <Button variant="outlined" onClick={handleClearFilters} size="small" startIcon={<ClearIcon />}>
                                Clear All
                            </Button>
                        )}
                    </Box>

                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Search users..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /></InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.isActive}
                                    onChange={(e) => handleFilterChange('isActive', e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="true">Active</MenuItem>
                                    <MenuItem value="false">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Filter by country"
                                value={filters.country}
                                onChange={(e) => handleFilterChange('country', e.target.value)}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                    value={filters.sortBy}
                                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                    label="Sort By"
                                >
                                    <MenuItem value="createdAt">Creation Date</MenuItem>
                                    <MenuItem value="email">Email</MenuItem>
                                    <MenuItem value="profile.firstName">First Name</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Users Content */}
            <Paper>
                <Box sx={{ p: { xs: 2, sm: 3 }, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Users ({totalUsers})</Typography>
                    {isSmallMobile && (
                        <Button
                            variant="outlined"
                            onClick={() => setFiltersOpen(true)}
                            startIcon={<FilterIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
                            size="small"
                        >
                            Filters
                        </Button>
                    )}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: { xs: 300, sm: 400 } }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {viewMode === 'table' && !isMobile ? (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>User</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Phone</TableCell>
                                            <TableCell>Country</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Created</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ py: { xs: 4, sm: 8 } }}>
                                                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                        No users found
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                        {hasActiveFilters ? 'Try adjusting your filters' : 'No users available'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            users.map((user) => (
                                                <TableRow key={user._id} hover>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <Avatar sx={{ mr: { xs: 1, sm: 2 }, width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                                                                {user.profile?.firstName?.charAt(0)}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                                                    {user.profile?.firstName} {user.profile?.familyName}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                    ID: {user._id}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center">
                                                            <EmailIcon sx={{ mr: 1, fontSize: { xs: 16, sm: 20 }, color: 'text.secondary' }} />
                                                            <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                {user.email}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center">
                                                            <PhoneIcon sx={{ mr: 1, fontSize: { xs: 16, sm: 20 }, color: 'text.secondary' }} />
                                                            <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                {user.profile?.phone}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center">
                                                            <LocationIcon sx={{ mr: 1, fontSize: { xs: 16, sm: 20 }, color: 'text.secondary' }} />
                                                            <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                {user.profile?.country}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={getUserStatus(user).label}
                                                            color={getUserStatus(user).color}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            {formatDate(user.createdAt)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                                                            <IconButton
                                                                onClick={() => fetchUserDetails(user._id)}
                                                                size="small"
                                                                color="primary"
                                                            >
                                                                <ViewIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                onClick={() => {
                                                                    setSelectedUser(user);
                                                                    setStatusUpdateDialog(true);
                                                                }}
                                                                size="small"
                                                                color={user.isActive ? 'error' : 'success'}
                                                            >
                                                                {user.isActive ? <BlockIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                                {users.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
                                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                            No users found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            {hasActiveFilters ? 'Try adjusting your filters' : 'No users available'}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                                        {users.map((user) => (
                                            <Grid item xs={12} sm={6} md={4} lg={3} key={user._id}>
                                                <UserCard
                                                    user={user}
                                                    onView={() => fetchUserDetails(user._id)}
                                                    onToggleStatus={(user) => {
                                                        setSelectedUser(user);
                                                        setStatusUpdateDialog(true);
                                                    }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Box>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(event, value) => setPage(value)}
                                    color="primary"
                                    size={isSmallMobile ? 'small' : 'medium'}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Paper>

            {/* User Details Dialog */}
            <UserDetailDialog
                open={userDetailDialog}
                onClose={() => {
                    setUserDetailDialog(false);
                    setSelectedUser(null);
                    setUserDetailTab(0);
                }}
                user={selectedUser}
                orders={userOrders}
                cart={userCart}
                tabValue={userDetailTab}
                onTabChange={(e, v) => setUserDetailTab(v)}
            />

            {/* Status Update Dialog */}
            <Dialog open={statusUpdateDialog} onClose={() => setStatusUpdateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ p: { xs: 2, sm: 3 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Confirm Status Update</DialogTitle>
                <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Are you sure you want to <strong>{selectedUser?.isActive ? 'deactivate' : 'activate'}</strong> this user?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        User: {selectedUser?.profile?.firstName} {selectedUser?.profile?.familyName} ({selectedUser?.email})
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                    <Button onClick={() => setStatusUpdateDialog(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => updateUserStatus(selectedUser?._id, !selectedUser?.isActive)}
                        color={selectedUser?.isActive ? 'error' : 'success'}
                        variant="contained"
                    >
                        {selectedUser?.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Users;